'use client';

import { FC, useEffect, useRef } from 'react';
import Cookies from 'universal-cookie';

import { COOKIE_CHAT_STARTED_FROM_GREETING } from '@/constants/cookie.constants';
import { getCookieExpiryChatStartedFromGreeting } from '@/general/helpers/getCookieExpiry';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

type EventType = 'displayed' | 'hidden' | 'chat_started';

const cookies = new Cookies();

export const ChatEventsListener: FC = () => {
  const currentGreetingIdRef = useRef<number | null>(null);
  const sentEventTypesRef = useRef<Set<EventType>>(new Set());

  useEffect(() => {
    const sendEventOnce = (eventType: EventType, data?: { chat_group?: number }) => {
      console.log('sendEventOnce', eventType, data);
      if (!sentEventTypesRef.current.has(eventType)) {
        sentEventTypesRef.current.add(eventType);
        pushToDataLayer({
          event: 'chat_auto_prompt',
          event_type: eventType,
          ...data,
        });

        // Set cookie when chat is started from a greeting
        if (eventType === 'chat_started') {
          cookies.set(COOKIE_CHAT_STARTED_FROM_GREETING, 'true', {
            path: '/',
            secure: process.env.NODE_ENV === 'production',
            expires: getCookieExpiryChatStartedFromGreeting(),
            sameSite: 'lax',
          });
        }
      }
    };

    const handleGreetingDisplayed = (greeting: { id: number; uniqueId: string }) => {
      currentGreetingIdRef.current = greeting.id;
      sendEventOnce('displayed');
    };

    const handleGreetingHidden = () => {
      currentGreetingIdRef.current = null;
      sendEventOnce('hidden');
    };

    const handleNewEvent = (event: {
      timestamp: number;
      type: 'message' | 'rich_message' | 'file';
      author: { id: string; type: 'customer' | 'agent' };
      greeting?: { id: number; uniqueId: string };
    }) => {
      // Detect when customer starts chatting from a greeting
      if (
        event.author.type === 'customer' &&
        currentGreetingIdRef.current !== null &&
        (event.type === 'message' || event.type === 'rich_message')
      ) {
        const greetingId = currentGreetingIdRef.current;
        sendEventOnce('chat_started', { chat_group: greetingId });
        currentGreetingIdRef.current = null;
      }
    };

    globalThis.window.LiveChatWidget?.on('greeting_displayed', handleGreetingDisplayed);
    globalThis.window.LiveChatWidget?.on('greeting_hidden', handleGreetingHidden);
    globalThis.window.LiveChatWidget?.on('new_event', handleNewEvent);

    return () => {
      globalThis.window.LiveChatWidget?.off('greeting_displayed', handleGreetingDisplayed);
      globalThis.window.LiveChatWidget?.off('greeting_hidden', handleGreetingHidden);
      globalThis.window.LiveChatWidget?.off('new_event', handleNewEvent);
    };
  }, []);

  return null;
};
